import { cn } from '@/utils/cn'
import { colorFromString, initials } from '@/utils/format'

interface AvatarProps {
  name: string
  src?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  ring?: boolean
  /** Override the initials background (defaults to a colour derived from the name). */
  color?: string
}

const sizes = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-xl',
}

export function Avatar({ name, src, size = 'md', className, ring, color }: AvatarProps) {
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white',
        sizes[size],
        ring && 'ring-2 ring-white',
        className,
      )}
      style={!src ? { backgroundColor: color ?? colorFromString(name) } : undefined}
      title={name}
    >
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        initials(name)
      )}
    </span>
  )
}

export function AvatarGroup({
  names,
  max = 4,
  size = 'sm',
}: {
  names: { name: string; src?: string }[]
  max?: number
  size?: AvatarProps['size']
}) {
  const shown = names.slice(0, max)
  const extra = names.length - shown.length
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((n, i) => (
        <Avatar key={i} name={n.name} src={n.src} size={size} ring />
      ))}
      {extra > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-slate-200 font-semibold text-slate-600 ring-2 ring-white',
            sizes[size ?? 'sm'],
          )}
        >
          +{extra}
        </span>
      )}
    </div>
  )
}
